/**
 * features/comms/CommsHub.tsx
 * ------------------------------------------------------------------
 * Phase 1 — Communications hub. Compose and send branded email from
 * the Acepsle operator account via the sendAcepsleEmail callable, with
 * To/CC/BCC recipients, a template picker, and attachments. Recent
 * sends are listed from the acepsleEmailLog audit collection.
 * ------------------------------------------------------------------
 */
import { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Mail, Paperclip, Send, X, CheckCircle2, AlertCircle, Plus } from 'lucide-react';
import { functions, db } from '@/lib/firebase';
import {
  EMAIL_TEMPLATES,
  getTemplate,
  renderBrandedHtml,
  substitute,
} from '@/lib/templates';
import { COLLECTIONS, type EmailLogEntry } from '@/types';
import { Button, Card, Field, Input, Textarea, Select, Badge } from '@/components/ui/primitives';

interface Attachment {
  filename: string;
  contentBase64: string;
  contentType: string;
}

interface SendPayload {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  html?: string;
  templateId?: string;
  attachments?: Attachment[];
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

const sendEmail = httpsCallable<SendPayload, { ok: boolean }>(functions, 'sendAcepsleEmail');

export function CommsHub() {
  const [templateId, setTemplateId] = useState('blank');
  const [recipientName, setRecipientName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [proposalUrl, setProposalUrl] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [branded, setBranded] = useState(true);
  const [files, setFiles] = useState<File[]>([]);

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const [log, setLog] = useState<EmailLogEntry[]>([]);

  // Recent sends
  useEffect(() => {
    const q = query(
      collection(db, COLLECTIONS.emailLog),
      orderBy('sentAt', 'desc'),
      limit(25),
    );
    return onSnapshot(
      q,
      (snap) => {
        setLog(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<EmailLogEntry, 'id'>) })));
      },
      () => setLog([]),
    );
  }, []);

  function applyTemplate(id: string) {
    setTemplateId(id);
    const t = getTemplate(id);
    if (!t || id === 'blank') return;
    const ctx = { recipientName, schoolName, proposalUrl };
    setSubject(substitute(t.subject, ctx));
    setBody(substitute(t.body, ctx));
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length === 0) return;
    setFiles((prev) => [...prev, ...picked].slice(0, 5));
    e.target.value = '';
  }

  const canSend = useMemo(
    () => to.trim().length > 0 && subject.trim().length > 0 && body.trim().length > 0 && !sending,
    [to, subject, body, sending],
  );

  async function handleSend() {
    setResult(null);
    setSending(true);
    try {
      const attachments: Attachment[] = [];
      for (const f of files) {
        attachments.push({
          filename: f.name,
          contentBase64: await fileToBase64(f),
          contentType: f.type || 'application/octet-stream',
        });
      }
      const payload: SendPayload = {
        to,
        subject: subject.trim(),
        body,
        ...(cc.trim() ? { cc } : {}),
        ...(bcc.trim() ? { bcc } : {}),
        ...(branded
          ? {
              // School Portal templates wear the School Portal look.
              html: renderBrandedHtml(body, {
                theme: templateId.startsWith('school-') ? 'schoolportal' : 'acepsle',
              }),
            }
          : {}),
        ...(templateId !== 'blank' ? { templateId } : {}),
        ...(attachments.length ? { attachments } : {}),
      };
      await sendEmail(payload);
      setResult({ ok: true, msg: 'Email sent.' });
      // Reset the compose form (keep recipient name for convenience).
      setTo('');
      setCc('');
      setBcc('');
      setSubject('');
      setBody('');
      setFiles([]);
      setTemplateId('blank');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Send failed.';
      setResult({ ok: false, msg });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      {/* Compose */}
      <Card accent className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brandSoft text-brand">
            <Mail size={18} />
          </div>
          <div>
            <h2 className="font-semibold text-primary dark:text-gray-100">Compose</h2>
            <p className="text-xs text-primaryLight dark:text-gray-400">
              Sends from info@acepslebotswana.com
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Template">
            <Select value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
              {EMAIL_TEMPLATES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Recipient name" hint="Used for {{recipientName}}">
            <Input
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="e.g. Mr Smith"
            />
          </Field>
        </div>

        {/* Proposal templates carry two more placeholders. These fields only
            appear once such a template is chosen, which is after it was
            first substituted — hence the explicit re-apply below, rather
            than auto-rewriting a body the user may already have edited. */}
        {templateId.startsWith('school-') && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="School / organisation" hint="Used for {{schoolName}}">
              <Input
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                placeholder="e.g. The Learning Centre"
              />
            </Field>
            <Field label="Proposal link" hint="Used for {{proposalUrl}}">
              <Input
                value={proposalUrl}
                onChange={(e) => setProposalUrl(e.target.value)}
                placeholder="https://hub.acepslebotswana.com/proposals/…"
              />
            </Field>
            <div className="sm:col-span-2">
              <Button
                variant="secondary"
                onClick={() => applyTemplate(templateId)}
                icon={<Plus size={15} />}
              >
                Re-apply template with these details
              </Button>
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Overwrites the subject and message below — do this before editing them.
              </p>
            </div>
          </div>
        )}

        <Field label="To" hint="One or more emails, comma-separated">
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@example.com" />
        </Field>

        {!showCcBcc ? (
          <button
            type="button"
            onClick={() => setShowCcBcc(true)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
          >
            <Plus size={13} /> Add Cc / Bcc
          </button>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Cc">
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="comma-separated" />
            </Field>
            <Field label="Bcc">
              <Input value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="comma-separated" />
            </Field>
          </div>
        )}

        <Field label="Subject">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Field>

        <Field label="Message">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            placeholder="Write your message…"
          />
        </Field>

        {/* Attachments */}
        <div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">
              Attachments
            </span>
            <label className="inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-brand hover:underline">
              <Paperclip size={13} /> Add file
              <input type="file" multiple className="hidden" onChange={onPickFiles} />
            </label>
          </div>
          {files.length > 0 && (
            <ul className="mt-2 space-y-1">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center justify-between rounded-lg bg-black/5 px-3 py-1.5 text-sm dark:bg-white/5"
                >
                  <span className="truncate">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                    className="text-primaryLight hover:text-brand"
                    aria-label="Remove"
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Up to 5 files, 10 MB each.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-primary dark:text-gray-200">
          <input
            type="checkbox"
            checked={branded}
            onChange={(e) => setBranded(e.target.checked)}
            className="h-4 w-4 accent-brand"
          />
          Send as branded HTML (School Portal look for School Portal templates, Acepsle otherwise)
        </label>

        {result && (
          <div
            className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${
              result.ok
                ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-300'
                : 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300'
            }`}
          >
            {result.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {result.msg}
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSend} loading={sending} disabled={!canSend} icon={<Send size={16} />}>
            Send email
          </Button>
        </div>
      </Card>

      {/* Recent sends */}
      <Card className="space-y-3">
        <h2 className="font-semibold text-primary dark:text-gray-100">Recent sends</h2>
        {log.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No emails sent yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {log.map((e) => (
              <li
                key={e.id}
                className="rounded-xl border border-black/5 p-3 dark:border-white/10"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-primary dark:text-gray-100">
                    {e.subject || '(no subject)'}
                  </span>
                  <Badge tone={e.status === 'sent' ? 'success' : 'danger'}>{e.status}</Badge>
                </div>
                <div className="mt-0.5 truncate text-xs text-primaryLight dark:text-gray-400">
                  To: {e.to?.join(', ')}
                </div>
                <div className="mt-0.5 text-xs text-gray-400">
                  {e.sentAt ? new Date(e.sentAt).toLocaleString() : ''}
                  {e.attachments && e.attachments.length > 0
                    ? ` · ${e.attachments.length} attachment${e.attachments.length > 1 ? 's' : ''}`
                    : ''}
                </div>
                {e.status === 'failed' && e.error && (
                  <div className="mt-1 text-xs text-red-600 dark:text-red-400">{e.error}</div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
